/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  ConflictResponse,
  CreateTopupBody,
  ForbiddenResponse,
  InternalErrorResponse,
  ListTopupsParams,
  NotFoundResponse,
  PayloadTooLargeResponse,
  TooManyRequestsResponse,
  Topup,
  TopupPage,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getCreateTopupUrl = () => {




  return `/topups`
}

/**
 * Raises the invoice and the payment, then asks the operator. The
 * answer carries the operator's first outcome: a pending payment, an
 * Orange `redirectUrl` to follow, or an `initiationError`. For a bank
 * transfer use `POST /transfers`. Velocity limits apply (429
 * `topup_rate_limited`).
 * @summary Buy credit with mobile money
 */
export const createTopup = async (createTopupBody: CreateTopupBody, options?: Parameters<typeof apiFetch>[1]): Promise<Topup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Topup>(getCreateTopupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createTopupBody)
  }
);}





export const getCreateTopupMutationKey = () => ['createTopup'] as const;

export const getCreateTopupMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTopup>>, TError,CreateTopupMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTopup>>, TError,CreateTopupMutationVariables, TContext> => {

const mutationKey = getCreateTopupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTopup>>, CreateTopupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTopup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTopupMutationResult = NonNullable<Awaited<ReturnType<typeof createTopup>>>
    export type CreateTopupMutationBody = CreateTopupBody
    export type CreateTopupMutationError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type CreateTopupMutationVariables = {data: CreateTopupBody}

    /**
 * @summary Buy credit with mobile money
 */
export const useCreateTopup = <TError = UnauthorizedResponse | ForbiddenResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTopup>>, TError,CreateTopupMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createTopup>>,
        TError,
        CreateTopupMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTopupMutationOptions(options), queryClient);
    }
    export const getListTopupsUrl = (params?: ListTopupsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/topups?${stringifiedParams}` : `/topups`
}

/**
 * @summary The caller's top-ups, newest first
 */
export const listTopups = async (params?: ListTopupsParams, options?: Parameters<typeof apiFetch>[1]): Promise<TopupPage> => {

  return apiFetch<TopupPage>(getListTopupsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTopupsInfiniteQueryKey = (params?: ListTopupsParams,) => {
    return [
    'infinite', `/topups`, ...(params ? [params] : [])
    ] as const;
    }

export const getListTopupsQueryKey = (params?: ListTopupsParams,) => {
    return [
    `/topups`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTopupsInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof listTopups>>, ListTopupsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListTopupsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData, QueryKey, ListTopupsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTopupsInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTopups>>, QueryKey, ListTopupsParams['cursor']> = ({ signal, pageParam }) => listTopups({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData, QueryKey, ListTopupsParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTopupsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof listTopups>>>
export type ListTopupsInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListTopupsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listTopups>>, ListTopupsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListTopupsParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData, QueryKey, ListTopupsParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTopups>>,
          TError,
          Awaited<ReturnType<typeof listTopups>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTopupsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listTopups>>, ListTopupsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListTopupsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData, QueryKey, ListTopupsParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTopups>>,
          TError,
          Awaited<ReturnType<typeof listTopups>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTopupsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listTopups>>, ListTopupsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListTopupsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData, QueryKey, ListTopupsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's top-ups, newest first
 */

export function useListTopupsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listTopups>>, ListTopupsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListTopupsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData, QueryKey, ListTopupsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTopupsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getListTopupsQueryOptions = <TData = Awaited<ReturnType<typeof listTopups>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListTopupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTopupsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTopups>>> = ({ signal }) => listTopups(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTopupsQueryResult = NonNullable<Awaited<ReturnType<typeof listTopups>>>
export type ListTopupsQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListTopups<TData = Awaited<ReturnType<typeof listTopups>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListTopupsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTopups>>,
          TError,
          Awaited<ReturnType<typeof listTopups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTopups<TData = Awaited<ReturnType<typeof listTopups>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListTopupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTopups>>,
          TError,
          Awaited<ReturnType<typeof listTopups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTopups<TData = Awaited<ReturnType<typeof listTopups>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListTopupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's top-ups, newest first
 */

export function useListTopups<TData = Awaited<ReturnType<typeof listTopups>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListTopupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTopups>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTopupsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGetTopupUrl = (id: string,) => {




  return `/topups/${id}`
}

/**
 * @summary One top-up, with its payment's status
 */
export const getTopup = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Topup> => {

  return apiFetch<Topup>(getGetTopupUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTopupQueryKey = (id: string,) => {
    return [
    `/topups/${id}`
    ] as const;
    }


export const getGetTopupQueryOptions = <TData = Awaited<ReturnType<typeof getTopup>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopup>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTopupQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTopup>>> = ({ signal }) => getTopup(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTopup>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTopupQueryResult = NonNullable<Awaited<ReturnType<typeof getTopup>>>
export type GetTopupQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useGetTopup<TData = Awaited<ReturnType<typeof getTopup>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopup>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTopup>>,
          TError,
          Awaited<ReturnType<typeof getTopup>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTopup<TData = Awaited<ReturnType<typeof getTopup>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopup>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTopup>>,
          TError,
          Awaited<ReturnType<typeof getTopup>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTopup<TData = Awaited<ReturnType<typeof getTopup>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopup>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One top-up, with its payment's status
 */

export function useGetTopup<TData = Awaited<ReturnType<typeof getTopup>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTopup>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTopupQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






